import { Overlay, OverlayRef } from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'
import { ComponentRef, DestroyRef, inject, Injectable, signal, TemplateRef, ViewContainerRef } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'
import { filter } from 'rxjs'
import { PortalService } from '../portal/portal.service'
import { DrawerOutlet } from './drawer.component'

export const DrawerPositions = ['left', 'right'] as const
export type DrawerPosition = (typeof DrawerPositions)[number]

export interface DrawerConfig {
  content: TemplateRef<any>
  drawerId: string
  position: DrawerPosition
  viewContainerRef: ViewContainerRef
}

/**
 * This service handles the creation, positioning, and lifecycle of drawer overlays.
 *
 * Note that this service is used internally by the {@link DrawerDirective} which should be able
 * to handle most use cases for toggling a drawer open and closed when clicking a button.
 */
@Injectable({
  providedIn: 'root',
})
export class DrawerService {
  private readonly destroyRef = inject(DestroyRef)
  private readonly overlay = inject(Overlay)
  private readonly portalService = inject(PortalService)
  private readonly router = inject(Router)

  readonly currentDrawerId = signal<string | null>(null)
  private overlayRef: OverlayRef | null = null
  private portal: ComponentPortal<DrawerOutlet> | null = null
  private componentRef: ComponentRef<DrawerOutlet> | null = null
  private overlayEventsCleanup: (() => void)[] = []

  open(config: DrawerConfig): void {
    if (this.currentDrawerId()) {
      this.close()
    }

    this.portalService.setContent(undefined)

    this.overlayRef = this.createOverlayRef(config.position)
    this.setUpOverlayEvents()

    this.portal = new ComponentPortal(DrawerOutlet, config.viewContainerRef)
    this.componentRef = this.overlayRef.attach(this.portal)
    this.componentRef.setInput('id', config.drawerId)
    this.componentRef.setInput('content', config.content)
    this.componentRef.setInput('position', config.position)

    this.currentDrawerId.set(config.drawerId)
  }

  close(): void {
    this.currentDrawerId.set(null)
    this.componentRef = null
    this.portal = null
    this.removeOverlayEvents()
    this.overlayRef?.dispose()
    this.overlayRef = null
  }

  private createOverlayRef(position: DrawerPosition): OverlayRef {
    const basePositionStrategy = this.overlay.position().global().top('0')

    return this.overlay.create({
      positionStrategy: position === 'left' ? basePositionStrategy.left('0') : basePositionStrategy.right('0'),
      height: '100%',
      hasBackdrop: true,
      backdropClass: 'drawer-backdrop',
    })
  }

  private setUpOverlayEvents(): void {
    if (!this.overlayRef) return

    this.removeOverlayEvents()

    const backdropClick = this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.close())

    const keydown = this.overlayRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event.key === 'Escape') {
          this.close()
        }
      })

    this.overlayEventsCleanup = [() => backdropClick.unsubscribe(), () => keydown.unsubscribe()]
  }

  private removeOverlayEvents(): void {
    this.overlayEventsCleanup.forEach(cleanup => cleanup())
    this.overlayEventsCleanup = []
  }

  private readonly closeOnNavigationChange = this.router.events
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(event => event instanceof NavigationEnd),
    )
    .subscribe(() => {
      if (this.currentDrawerId()) {
        this.close()
      }
    })
}
